import { useCallback, useEffect, useMemo, useRef, useState, isValidElement, createElement } from "react";
import { useRouter } from "next/router";
import { BiSolidEditAlt, BiSolidPlaneAlt, BiSort } from "react-icons/bi";
import { BsTaxiFrontFill } from "react-icons/bs";
import { FaHotel, FaPassport, FaStar } from "react-icons/fa6";
import { GiAirBalloon } from "react-icons/gi";
import {
  IoArrowBackSharp,
  IoCalendarClear,
  IoCalendarOutline,
  IoChevronBack,
  IoChevronDown,
  IoClose,
  IoCloseOutline,
  IoLocationOutline,
  IoPencil,
  IoSwapVertical,
} from "react-icons/io5";
import { HiOutlineAdjustmentsHorizontal } from "react-icons/hi2";
import { SiMealie } from "react-icons/si";
import MainLayout from "@/Component/MainLayout";
import Breadcrumbs from "@/Component/Common/Breadcrumbs";
import featuredStyles from "@/Component/Common/FeaturedPackage/FeaturedPackage.module.css";
import styles from "./HolidayListing.module.css";
import useIsDesktop from "@/hooks/useIsDesktop";
import useBodyScrollLock from "@/hooks/useBodyScrollLock";
import useHolidayEditSearch from "@/hooks/useHolidayEditSearch";
import DesktopListingSearch from "./DesktopListingSearch";
import LocationSearchScreen from "./LocationSearchScreen";
import {
  getHeaderSearchSummary,
  isSameSearchDestination,
  resolveEffectiveSearchMonth,
  resolveSearchMonthLabel,
} from "@/utils/holidaySearch";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { fetchHolidayFilters, fetchHolidaysList } from "@/store/slices/holidaySlice";
import {
  buildFilterState,
  buildHolidayListPayload,
  buildReviewBands,
  getUrlFilterDefaults,
  getUrlPageTitle,
  normalizeHolidayFilters,
  parseUrlListParams,
  pickUrlApiParams,
} from "@/utils/holidayListing";
import { formatIndianPrice } from "@/utils/formatIndianPrice";
import { getCurrencySymbol } from "@/utils/getCurrencySymbol";
import { withRegionPath } from "@/utils/session";
import {
  canRestoreListingRange,
  clampFilterRange,
  consumeListingSearchReset,
  getFilterBoundsKey,
  getListingSearchKey,
  isSameListingSearch,
  loadListingFilters,
  mergePresentUrlDefaults,
  saveListingFilters,
  snapInclusiveRangeValue,
  useListingSearchEpoch,
} from "@/utils/listingFilterSession";
import { INCLUSION_ICONS as FEATURED_INCLUSION_ICONS } from "@/utils/Inclusions";
import CartNavLink from "@/Component/Common/CartNavLink";
import DownloadNH from "@/Component/Common/DownloadNH";
import SeeTheWorld from "@/Component/Common/SeeTheWorld";
import { MdLocationOn } from "react-icons/md";
import PackageCountdown from "@/Component/Common/PackageCountdown";
import { getCountdownEndMs, getReviewCount, getSeatsLeftLabel } from "@/utils/packageCardMeta";

const PAGE_SIZE = 10;

const INCLUSION_ICONS = [
  { id: "flight", icon: BiSolidPlaneAlt },
  { id: "meal", icon: SiMealie },
  { id: "transfer", icon: BsTaxiFrontFill },
  { id: "hotel", icon: FaHotel },
  { id: "visa", icon: FaPassport },
  { id: "activity", icon: GiAirBalloon },
];

const EMPTY_SELECTION = {
  packageTypeIds: [],
  accommodationTypeIds: [],
  travelStyleIds: [],
  cityIds: [],
  cancellationPolicyIds: [],
  flightValues: [],
  nightOptionValues: [],
  reviewRating: 0,
  hotelRatings: [],
  visaIncluded: "",
  activityIncluded: "",
  availableMonths: [],
};

function toggleId(list, id) {
  return list.includes(id) ? list.filter((item) => item !== id) : [...list, id];
}

function toggleValue(list, value) {
  return list.includes(value) ? list.filter((item) => item !== value) : [...list, value];
}

function selectSingleValue(list, value) {
  return list.includes(value) ? [] : [value];
}

function mapHolidayPackage(pkg) {
  const nights = pkg.totalNights || pkg.nights || 0;
  const days = nights > 0 ? nights + 1 : 0;
  const cities = Array.isArray(pkg.cities) ? pkg.cities : [];

  return {
    id: pkg.packageId,
    title: pkg.packageName || "",
    categories: pkg.packageTypeName || "",
    cities,
    duration: nights ? `${nights}N / ${days}D` : "",
    location: cities[0]?.city?.cityName || "",
    rating: Number(pkg.reviews?.averageRating) || Number(pkg.rating) || 0,
    ratingLabel: pkg.reviews?.reviewLabel || pkg.reviewRemark || "",
    reviews: getReviewCount(pkg),
    seatsLeft: getSeatsLeftLabel(pkg),
    isCountdownShow: pkg.isCountdownShow,
    countdownEndDate: pkg.countdownEndDate,
    countDownEndDate: pkg.countDownEndDate,
    countdownDate: pkg.countdownDate,
    countDownDate: pkg.countDownDate,
    offerEndDate: pkg.offerEndDate,
    offerExpiryDate: pkg.offerExpiryDate,
    timerEndDate: pkg.timerEndDate,
    countdownExpiryDate: pkg.countdownExpiryDate,
    countDownExpiryDate: pkg.countDownExpiryDate,
    countDown: pkg.countDown,
    countdown: pkg.countdown,
    tagOnThumbnail: String(pkg.tagOnThumbnail || "").trim(),
    discountLabel: pkg.discountTag || pkg.tag2 || pkg.couponTag || "",
    priceOld: Number(pkg.displayCutPrice) || Number(pkg.price) || 0,
    price: Number(pkg.price) || 0,
    image: pkg.imagePath || pkg.mobImagePath || "",
    href: withRegionPath(`/holiday/${pkg.slug}`),
    isIndia: Boolean(pkg.isIndia),
    inclusions: {
      flight: Boolean(pkg.isFlightIncluded),
      meal: Boolean(pkg.isMealsIncluded),
      transfer: Boolean(pkg.isTransferIncluded),
      hotel: Boolean(pkg.isHotelIncluded),
      visa: Boolean(pkg.isVisaIncluded),
      activity: Boolean(pkg.isActivityIncluded),
    },
    flightSubText: String(pkg.flightSubText || "").trim(),
  };
}

function renderInclusionIcon({ id, icon }, flightSubText, className) {
  const iconNode = isValidElement(icon)
    ? icon
    : typeof icon === "function"
      ? createElement(icon)
      : icon;

  if (id === "flight" && flightSubText) {
    return (
      <span key={id} className={`${className} ${styles.flightInclusion}`}>
        <span className={styles.flightSubTextTip}>{flightSubText}</span>
        {iconNode}
      </span>
    );
  }

  return (
    <span key={id} className={className}>
      {iconNode}
    </span>
  );
}

function PriceDisplay({ value, variant = "main", size = 14 }) {
  return (
    <>
      {getCurrencySymbol(variant, size)}
      {formatIndianPrice(value)}
    </>
  );
}

function CardImageOverlays({ item, tagOnThumbnail, duration, href }) {
  return (
    <>
      {tagOnThumbnail ? (
        <span className={styles.thumbnailTag}>
          <FaStar className={styles.thumbnailTagIcon} aria-hidden="true" />
          <span className={styles.thumbnailTagText}>{tagOnThumbnail}</span>
        </span>
      ) : null}
      {duration ? <span className={styles.durationBadge}>{duration}</span> : null}
      {getCountdownEndMs(item) ? <span className={styles.countDown}><PackageCountdown item={item} className={featuredStyles.cardTimer} /></span> : null}
      {/* <a href={href} className={styles.mapBtn} aria-label="View package details">
        <FaMap aria-hidden="true" />
      </a> */}
    </>
  );
}

function ResponsivePackageCard({ item }) {
  const activeInclusions = FEATURED_INCLUSION_ICONS.filter(({ id }) => item.inclusions?.[id]);
  const cityLine = item.cities
    .map((city) => `${city.nights}N ${city?.city?.cityName || ""}`.trim())
    .filter(Boolean)
    .join(" | ");
  const durationLabel = item.duration ? item.duration.replace("/", " / ") : "";

  return (
    <article className={`${featuredStyles.card} ${styles.responsiveCard}`}>
      <a href={item.href} className={featuredStyles.imageLink} aria-label={item.title}>
        <div className={featuredStyles.imageWrap}>
          <img src={item.image} alt={item.title} className={featuredStyles.cardImage} />

          {item.tagOnThumbnail ? (
            <span className={featuredStyles.badgeBest}>
              <FaStar style={{ marginTop: "-2px" }} />
              {item.tagOnThumbnail}
            </span>
          ) : null}
          {item.seatsLeft ? (
            <span className={featuredStyles.badgeSeats}>{item.seatsLeft}</span>
          ) : null}

          {durationLabel ? <span className={featuredStyles.duration}>{durationLabel}</span> : null}

          {item.rating ? (
            <span className={featuredStyles.rating}>
              <strong style={{ color: "#0B192C", marginTop: "2px" }}>{item.rating}</strong>
              <FaStar color="#FFB835" />
              {item.reviews != null ? ` (${item.reviews})` : null}
            </span>
          ) : null}
        </div>
      </a>

      <div className={featuredStyles.body}>
        <h3 className={featuredStyles.cardTitle}>
          <a href={item.href} className={featuredStyles.titleLink}>{item.title}</a>
        </h3>
        {cityLine ? <p className={featuredStyles.cardSubtitle}>{cityLine}</p> : null}

        <div className={featuredStyles.cardIncludes}>
          <div className={featuredStyles.inclusion}>
            {activeInclusions.map((inclusion) => (
              <span key={inclusion.id} className={featuredStyles.includeIcon}>
                {(inclusion.id === "flight" && item.flightSubText) ?
                  <span className={featuredStyles.flightSubTextTip}>{item.flightSubText}</span>
                  :
                  null
                }
                {inclusion.icon}
              </span>
            ))}
          </div>
          <PackageCountdown item={item} className={featuredStyles.cardTimer} />
        </div>

        <div className={featuredStyles.footer}>
          {item.discountLabel ? (
            <span className={featuredStyles.discountTag}>{item.discountLabel}</span>
          ) : null}
          <div className={featuredStyles.footerContent}>
            <div className={featuredStyles.priceBlock}>
              <p className={featuredStyles.starting}>
                Starting From
                {item.priceOld ? (
                  <span className={featuredStyles.oldPrice}>
                    <PriceDisplay value={item.priceOld} variant="sub" size={10} />
                  </span>
                ) : null}
              </p>
              <p className={featuredStyles.price}>
                <PriceDisplay value={item.price} size={16} />
                <span className={featuredStyles.perPerson}>/Person</span>
              </p>
            </div>
            <a href={item.href} className={featuredStyles.viewDealsBtn}>
              View Details
            </a>
          </div>
        </div>
      </div>
    </article>
  );
}

function PackageCard({ item, mobile }) {
  const activeInclusions = FEATURED_INCLUSION_ICONS.filter(({ id }) => item.inclusions?.[id]);

  if (mobile) {
    return <ResponsivePackageCard item={item} />;
  }

  return (
    <article className={styles.card}>
      <div className={styles.cardContent}>
        <a href={item.href} className={styles.cardImage} aria-label={item.title}>
          <img src={item.image} alt={item.title} />
          <CardImageOverlays
            item={item}
            tagOnThumbnail={item.tagOnThumbnail}
            duration={item.duration}
            href={item.href}
          />
        </a>
        <div className={styles.cardDetails}>
          <div className={styles.cardTop}>
            <div className={styles.cardBasic}>
              {item.categories ? <p className={styles.categories}>{item.categories}</p> : null}
              <h3 className={styles.cardTitle}>
                <a href={item.href} className={styles.titleLink}>{item.title}</a>
              </h3>
              <p className={styles.itinerary}>
                {item.cities.map((city, index) => (
                  <span key={`${item.id}-d-${city?.city?.cityId || index}`}>
                    {index > 0 ? " | " : ""}
                    <span style={{ fontWeight: 600 }}>{city.nights}N</span> {city?.city?.cityName}
                  </span>
                ))}
              </p>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
              {item.seatsLeft ?
                <span className={styles.seatsLeft}>{item.seatsLeft}</span>
                :
                null
              }
              <div className={styles.ratingBlock}>
                <span className={styles.ratingScore}>{item.rating}</span>
                <div>
                  {item.ratingLabel ? <p className={styles.ratingLabel}>{item.ratingLabel}</p> : null}
                  <p className={styles.reviews}>{item.reviews} Reviews</p>
                </div>
              </div>
            </div>
          </div>
          {activeInclusions.length ? (
            <div className={styles.inclusionsRow}>
              {activeInclusions.map((inclusion) =>
                renderInclusionIcon(
                  inclusion,
                  item.flightSubText,
                  styles.inclusionIcon
                )
              )}
            </div>
          ) : null}
        </div>
      </div>
      <div className={styles.cardPrice}>
        {item.discountLabel ? <span className={styles.offerBadge}>{item.discountLabel}</span> : null}
        <div className={styles.startpriceOld}>
          Starts From
          <p className={styles.priceOld}>
            <PriceDisplay value={item.priceOld} variant="sub" size={11} />
          </p>
        </div>
        <p className={styles.priceNow}>
          <PriceDisplay value={item.price} size={18} />
        </p>
        <p className={styles.priceNote}>Per Person</p>
        <a href={item.href} className={styles.viewBtn}>
          View Details
        </a>
      </div>
    </article>
  );
}

function CheckboxList({
  options,
  selectedIds,
  onToggle,
  showIcon = false,
  initialLimit = 0,
}) {
  const [expanded, setExpanded] = useState(false);

  if (!options?.length) return null;

  const hasMore = initialLimit > 0 && options.length > initialLimit;
  const visibleOptions =
    hasMore && !expanded ? options.slice(0, initialLimit) : options;

  return (
    <>
      <ul className={styles.checkList}>
        {visibleOptions.map((option) => (
          <li key={option.id}>
            <label className={styles.checkRow}>
              <span className={styles.checkLabel}>
                {showIcon && option.icon ? (
                  <img src={option.icon} alt="" className={styles.themeIcon} />
                ) : null}
                {option.label}
              </span>
              <input
                type="checkbox"
                style={{ height: "18px", width: "18px", border: "1px solid #B8B8B8" }}
                checked={selectedIds.includes(option.id)}
                onChange={() => onToggle(option.id)}
              />
            </label>
          </li>
        ))}
      </ul>
      {hasMore ? (
        <button
          type="button"
          className={styles.viewMoreBtn}
          onClick={() => setExpanded((prev) => !prev)}
        >
          {expanded ? "View Less" : "View More"}
        </button>
      ) : null}
    </>
  );
}

function ToggleButtonGroup({ options, selectedValues, onToggle, single = false }) {
  if (!options?.length) return null;

  return (
    <div className={styles.flightToggle}>
      {options.map((option) => {
        const value = option.value ?? option.id;
        const active = selectedValues.includes(value);
        return (
          <button
            key={value}
            type="button"
            className={active ? styles.flightActive : ""}
            onClick={() => onToggle(value, single)}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

function CollapsibleBlock({ title, open, onToggle, children }) {
  return (
    <section className={styles.filterBlock}>
      <button
        type="button"
        className={styles.themeToggle}
        onClick={onToggle}
        aria-expanded={open}
      >
        <h3>{title}</h3>
        <span className={open ? styles.chevronUp : ""}>
          <IoChevronDown size={20} />
        </span>
      </button>
      {open ? children : null}
    </section>
  );
}

function getRangeLabelStyle(percent) {
  if (percent <= 10) {
    return { left: `${percent}%`, transform: "translateX(0)" };
  }
  if (percent >= 90) {
    return { left: `${percent}%`, transform: "translateX(-100%)" };
  }
  return { left: `${percent}%`, transform: "translateX(-50%)" };
}

function DualRangeSlider({ min, max, value, onChange, step = 1, formatLabel, fixedLabels = false }) {
  const [minValue, maxValue] = value;
  const [activeThumb, setActiveThumb] = useState("max");
  const range = max - min || 1;
  const minPercent = ((minValue - min) / range) * 100;
  const maxPercent = ((maxValue - min) / range) * 100;

  const handleMinChange = (event) => {
    const next = snapInclusiveRangeValue(event.target.value, min, max, step);
    setActiveThumb("min");
    onChange([Math.min(next, maxValue), maxValue]);
  };

  const handleMaxChange = (event) => {
    const next = snapInclusiveRangeValue(event.target.value, min, max, step);
    setActiveThumb("max");
    onChange([minValue, Math.max(next, minValue)]);
  };

  return (
    <div className={styles.dualRange}>
      <div className={styles.dualRangeTrack}>
        <div className={styles.dualRangeRail} aria-hidden="true" />
        <div
          className={styles.dualRangeFill}
          style={{ left: `${minPercent}%`, width: `${maxPercent - minPercent}%` }}
          aria-hidden="true"
        />
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={minValue}
          onChange={handleMinChange}
          onPointerDown={() => setActiveThumb("min")}
          className={`${styles.dualRangeInput} ${styles.dualRangeInputMin}`}
          style={{ zIndex: activeThumb === "min" ? 5 : 3 }}
          aria-label="Minimum value"
        />
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={maxValue}
          onChange={handleMaxChange}
          onPointerDown={() => setActiveThumb("max")}
          className={`${styles.dualRangeInput} ${styles.dualRangeInputMax}`}
          style={{ zIndex: activeThumb === "max" ? 5 : 3 }}
          aria-label="Maximum value"
        />
      </div>
      <div className={`${styles.dualRangeValues} ${fixedLabels ? styles.dualRangeValuesFixed : ""}`}>
        {fixedLabels ? (
          <>
            <span className={styles.dualRangeValue}>{formatLabel(minValue)}</span>
            <span className={styles.dualRangeValue}>{formatLabel(maxValue)}</span>
          </>
        ) : (
          <>
            <span className={styles.dualRangeValue} style={getRangeLabelStyle(minPercent)}>
              {formatLabel(minValue)}
            </span>
            <span className={styles.dualRangeValue} style={getRangeLabelStyle(maxPercent)}>
              {formatLabel(maxValue)}
            </span>
          </>
        )}
      </div>
    </div>
  );
}

function MobileSheet({
  title,
  onClose,
  onClear,
  children,
  footerLabel = "Apply",
  onApply,
  showFooter = true,
  footerLoading = false,
}) {
  return (
    <div className={styles.sheetRoot}>
      <button
        type="button"
        className={styles.sheetOverlay}
        aria-label="Close"
        onClick={onClose}
      />
      <div className={styles.sheet} role="dialog" aria-modal="true" aria-label={title}>
        <div className={styles.sheetGrab} aria-hidden="true" />
        <div className={styles.sheetHeader}>
          <h2>{title}</h2>
          {onClear ? (
            <button type="button" className={styles.sheetClearBtn} onClick={onClear}>
              Clear
            </button>
          ) : (
            <button type="button" className={styles.sheetCloseBtn} onClick={onClose} aria-label="Close">
              <IoCloseOutline />
            </button>
          )}
        </div>
        <div className={styles.sheetBody}>{children}</div>
        {showFooter && onApply ? (
          <div className={styles.sheetFooter}>
            <button
              type="button"
              className={styles.sheetApplyBtn}
              onClick={onApply}
              disabled={footerLoading}
            >
              {footerLoading ? (
                <span className={styles.searchBtnSpinner} aria-hidden="true" />
              ) : (
                footerLabel
              )}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
function SortSheet({ options, value, onChange, onClose, onApply }) {
  return (
    <MobileSheet title="Sort By" onClose={onClose} onApply={onApply}>
      <div className={styles.sortSheetList}>
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            className={`${styles.sortSheetOption} ${value === option.value ? styles.sortSheetOptionActive : ""
              }`}
            onClick={() => onChange(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>
    </MobileSheet>
  );
}

function RangeFilterSheet({ title, children, onClose, onApply }) {
  return (
    <MobileSheet title={title} onClose={onClose} onApply={onApply}>
      <div className={styles.quickFilterSheet}>{children}</div>
    </MobileSheet>
  );
}

function ListingLoader({ fullPage = false, compact = false }) {
  return (
    <div
      className={`${styles.loadingWrap} ${fullPage ? styles.loadingWrapFull : ""} ${compact ? styles.loadingWrapBottom : ""
        }`}
    >
      <img src="/assets/loading.gif" alt="Loading" className={styles.loadingGif} />
    </div>
  );
}

function ListLoadSentinel({ hasMore, loading, loadingMore, onLoadMore }) {
  const sentinelRef = useRef(null);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore || loading || loadingMore) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) onLoadMore();
      },
      { root: null, rootMargin: "240px", threshold: 0 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore, onLoadMore]);

  if (!hasMore) return null;

  return (
    <div ref={sentinelRef} className={styles.listSentinel} aria-hidden="true">
      {loadingMore ? <ListingLoader compact /> : null}
    </div>
  );
}

function SearchSheet({
  destination,
  months,
  selectedMonth,
  onMonthChange,
  onDestinationClick,
  onClose,
  onApply,
  isSearching = false,
  monthError = false,
}) {
  return (
    <MobileSheet
      title="Edit Search"
      onClose={onClose}
      footerLabel="Search"
      onApply={onApply}
      footerLoading={isSearching}
    >
      <div className={styles.searchSheetFields}>
        <button
          type="button"
          className={`${styles.searchField} ${styles.searchFieldBtn}`}
          onClick={onDestinationClick}
        >
          <MdLocationOn size={20} color="#A4A4A4" />
          <span className={styles.searchFieldValue}>{destination || "Search destination"}</span>
        </button>
        <label className={`${styles.searchField} ${monthError ? styles.searchFieldError : ""}`.trim()}>
          <IoCalendarClear color="#A4A4A4" />
          <select
            value={selectedMonth}
            onChange={(event) => onMonthChange(event.target.value)}
            disabled={!months.length}
            aria-invalid={monthError}
          >
            <option value="">Select month</option>
            {months.map((month) => (
              <option key={month.id} value={month.value}>
                {month.label}
              </option>
            ))}
          </select>
        </label>
      </div>
    </MobileSheet>
  );
}

function FlightsSheet({ options, selectedValues, onToggle, onClose, onApply }) {
  return (
    <MobileSheet title="Flights" onClose={onClose} onApply={onApply}>
      <div className={styles.flightsSheetList}>
        {options.map((option) => {
          const value = option.value ?? option.id;
          const active = selectedValues.includes(value);
          return (
            <button
              key={value}
              type="button"
              className={`${styles.flightSheetOption} ${active ? styles.flightSheetOptionActive : ""
                }`}
              onClick={() => onToggle(value)}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </MobileSheet>
  );
}

function HotelRatingSheet({ options, selectedRatings, onToggle, onClose, onApply }) {
  return (
    <MobileSheet title="Hotel Rating" onClose={onClose} onApply={onApply}>
      <div className={styles.hotelRatingSheetRow}>
        {options.map((option) => {
          const stars = Number(option.value ?? option.id);
          const active = selectedRatings.includes(stars);
          return (
            <button
              key={option.id}
              type="button"
              className={`${styles.hotelRatingSheetBtn} ${active ? styles.hotelRatingSheetBtnActive : ""
                }`}
              onClick={() => onToggle(stars)}
              aria-pressed={active}
            >
              <FaStar /> {option.label}
            </button>
          );
        })}
      </div>
    </MobileSheet>
  );
}

function FiltersPanel({
  options,
  selection,
  ranges,
  openSections,
  handlers,
  onToggleSection,
  showApply,
  onApply,
  compact = false,
}) {
  const {
    onTogglePackageType,
    onToggleAccommodation,
    onToggleTravelStyle,
    onToggleCity,
    onToggleFlight,
    onToggleNightOption,
    onReviewRating,
    onToggleHotelRating,
    onToggleCancellationPolicy,
    onToggleVisa,
    onToggleActivity,
    onToggleMonth,
    onDuration,
    onBudget,
    onClear,
  } = handlers;

  const budgetStep =
    ranges.price.max - ranges.price.min > 1000
      ? Math.max(1, Math.round((ranges.price.max - ranges.price.min) / 100))
      : 1;

  return (
    <div className={`${styles.filtersInner} ${compact ? styles.filtersInnerCompact : ""}`}>
      {!compact ? (
        <div className={styles.filtersHead}>
          <h2>Filters</h2>
          <button type="button" className={styles.clearBtn} onClick={onClear}>
            Clear
          </button>
        </div>
      ) : null}

      <div className={styles.filterDesc}>
        {ranges.hasNightRange ? (
          <section className={styles.filterBlock}>
            <h3>Trip Duration</h3>
            <DualRangeSlider
              min={ranges.nights.min}
              max={ranges.nights.max}
              step={1}
              value={ranges.duration}
              onChange={onDuration}
              formatLabel={(val) => `${val}N`}
              fixedLabels
            />
          </section>
        ) : null}

        {/* {options.nightOptions.length ? (
          <section className={styles.filterBlock}>
            <h3>Duration Options</h3>
            <div className={styles.flightToggle}>
              {options.nightOptions.map((option) => {
                const value = Number(option.value ?? option.id);
                const active = selection.nightOptionValues.includes(value);
                return (
                  <button
                    key={option.id}
                    type="button"
                    className={active ? styles.flightActive : ""}
                    onClick={() => onToggleNightOption(value)}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </section>
        ) : null} */}

        {options.flights.length ? (
          <section className={styles.filterBlock}>
            <h3>Flights</h3>
            <ToggleButtonGroup
              options={options.flights}
              selectedValues={selection.flightValues}
              onToggle={onToggleFlight}
              single
            />
          </section>
        ) : null}

        {ranges.hasPriceRange ? (
          <section className={styles.filterBlock}>
            <h3>Budget</h3>
            <DualRangeSlider
              min={ranges.price.min}
              max={ranges.price.max}
              step={budgetStep}
              value={ranges.budget}
              onChange={onBudget}
              fixedLabels
              formatLabel={(val) => (
                <>
                  {getCurrencySymbol("main", 12)}
                  {formatIndianPrice(val)}
                </>
              )}
            />
          </section>
        ) : null}

        {options.packageTypes.length ? (
          <CollapsibleBlock
            title="Packages by theme"
            open={openSections.themes}
            onToggle={() => onToggleSection("themes")}
          >
            <CheckboxList
              options={options.packageTypes}
              selectedIds={selection.packageTypeIds}
              onToggle={onTogglePackageType}
            // showIcon
            />
          </CollapsibleBlock>
        ) : null}

        {options.accommodationTypes.length ? (
          <CollapsibleBlock
            title="Accommodation Types"
            open={openSections.accommodations}
            onToggle={() => onToggleSection("accommodations")}
          >
            <CheckboxList
              options={options.accommodationTypes}
              selectedIds={selection.accommodationTypeIds}
              onToggle={onToggleAccommodation}
            />
          </CollapsibleBlock>
        ) : null}

        {options.travelStyles.length ? (
          <CollapsibleBlock
            title="Travel styles"
            open={openSections.travelStyles}
            onToggle={() => onToggleSection("travelStyles")}
          >
            <CheckboxList
              options={options.travelStyles}
              selectedIds={selection.travelStyleIds}
              onToggle={onToggleTravelStyle}
            />
          </CollapsibleBlock>
        ) : null}

        {options.cities.length ? (
          <CollapsibleBlock
            title="Cities"
            open={openSections.cities}
            onToggle={() => onToggleSection("cities")}
          >
            <CheckboxList
              options={options.cities}
              selectedIds={selection.cityIds}
              onToggle={onToggleCity}
              initialLimit={10}
            />
          </CollapsibleBlock>
        ) : null}

        {options.reviewBands.length ? (
          <CollapsibleBlock
            title="Review & Rating"
            open={openSections.reviews}
            onToggle={() => onToggleSection("reviews")}
          >
            <ul className={styles.checkList}>
              {options.reviewBands.map((band) => (
                <li key={band.id}>
                  <label className={styles.checkRow}>
                    <span>{band.label}</span>
                    <input
                      type="checkbox"
                      checked={selection.reviewRating === band.min}
                      onChange={() =>
                        onReviewRating(selection.reviewRating === band.min ? 0 : band.min)
                      }
                    />
                  </label>
                </li>
              ))}
            </ul>
          </CollapsibleBlock>
        ) : null}

        {options.hotelRatings.length ? (
          <section className={styles.filterBlock}>
            <h3>Hotel Rating</h3>
            <div className={styles.hotelRatingRow}>
              {options.hotelRatings.map((option) => {
                const stars = Number(option.value ?? option.id);
                const active = selection.hotelRatings.includes(stars);
                return (
                  <button
                    key={option.id}
                    type="button"
                    className={`${styles.hotelRatingBtn} ${active ? styles.hotelRatingActive : ""
                      }`}
                    onClick={() => onToggleHotelRating(stars)}
                    aria-pressed={active}
                  >
                    <FaStar /> {option.label}
                  </button>
                );
              })}
            </div>
          </section>
        ) : null}

        {/* {options.cancellationPolicies.length ? (
          <CollapsibleBlock
            title="Cancellation Policy"
            open={openSections.cancellation}
            onToggle={() => onToggleSection("cancellation")}
          >
            <CheckboxList
              options={options.cancellationPolicies}
              selectedIds={selection.cancellationPolicyIds}
              onToggle={onToggleCancellationPolicy}
            />
          </CollapsibleBlock>
        ) : null}

        {options.visaIncluded.length ? (
          <section className={styles.filterBlock}>
            <h3>Visa Included</h3>
            <ToggleButtonGroup
              options={options.visaIncluded}
              selectedValues={selection.visaIncluded ? [selection.visaIncluded] : []}
              onToggle={onToggleVisa}
              single
            />
          </section>
        ) : null}

        {options.activityIncluded.length ? (
          <section className={styles.filterBlock}>
            <h3>Activity Included</h3>
            <ToggleButtonGroup
              options={options.activityIncluded}
              selectedValues={selection.activityIncluded ? [selection.activityIncluded] : []}
              onToggle={onToggleActivity}
              single
            />
          </section>
        ) : null}

        {options.availableMonths.length ? (
          <CollapsibleBlock
            title="Available Months"
            open={openSections.months}
            onToggle={() => onToggleSection("months")}
          >
            <CheckboxList
              options={options.availableMonths}
              selectedIds={selection.availableMonths}
              onToggle={onToggleMonth}
            />
          </CollapsibleBlock>
        ) : null} */}

        {showApply ? (
          <button type="button" className={styles.applyBtn} onClick={onApply}>
            Apply
          </button>
        ) : null}
      </div>
    </div>
  );
}

function getHolidayResetDefaults(urlParams = {}) {
  return {
    ...EMPTY_SELECTION,
    packageTypeIds: urlParams.packageTypeIds || [],
    accommodationTypeIds: urlParams.accommodationTypeIds || [],
    travelStyleIds: urlParams.travelStyleIds || [],
  };
}

function useFilterSelection(filterOptions, urlParams = {}, sessionKind) {
  const urlFilterDefaults = useMemo(() => getUrlFilterDefaults(urlParams), [urlParams]);
  const resetDefaults = useMemo(() => getHolidayResetDefaults(urlParams), [urlParams]);
  const searchKey = useMemo(
    () => getListingSearchKey(sessionKind, urlParams),
    [sessionKind, urlParams]
  );
  const searchEpoch = useListingSearchEpoch(sessionKind);
  const resetId = `${searchKey}#${searchEpoch}`;
  const allowRestoreRef = useRef(false);

  const [selection, setSelection] = useState(() => resetDefaults);
  const [duration, setDuration] = useState(null);
  const [budget, setBudget] = useState(null);
  const [appliedResetId, setAppliedResetId] = useState("");

  useEffect(() => {
    const stored = loadListingFilters(sessionKind);
    const storedEpoch = Number(stored?.epoch) || 0;
    const forcedReset = consumeListingSearchReset(sessionKind);
    const isNewSearch = forcedReset || searchEpoch > storedEpoch;
    const sameSearch =
      !isNewSearch && isSameListingSearch(stored, sessionKind, searchKey);

    if (sameSearch && stored?.selection && typeof stored.selection === "object") {
      setSelection(
        mergePresentUrlDefaults(
          {
            ...EMPTY_SELECTION,
            ...stored.selection,
          },
          urlFilterDefaults
        )
      );
      allowRestoreRef.current = true;
    } else {
      setSelection(resetDefaults);
      allowRestoreRef.current = false;
    }

    setDuration(null);
    setBudget(null);
    setAppliedResetId(resetId);
  }, [resetId, sessionKind, resetDefaults, urlFilterDefaults, searchEpoch, searchKey]);

  useEffect(() => {
    if (appliedResetId !== resetId) return;

    const stored = loadListingFilters(sessionKind);
    const nightMax = filterOptions.nights.max;
    const priceMax = filterOptions.price.max;
    const boundsKey = getFilterBoundsKey({
      nightMin: filterOptions.nights.min,
      nightMax,
      priceMin: filterOptions.price.min,
      priceMax,
    });
    const restore =
      allowRestoreRef.current && canRestoreListingRange(stored, boundsKey);

    if (nightMax > 0) {
      setDuration(
        restore
          ? clampFilterRange(stored?.duration, filterOptions.nights.min, nightMax)
          : [filterOptions.nights.min, nightMax]
      );
    }

    if (priceMax > 0) {
      setBudget(
        restore
          ? clampFilterRange(stored?.budget, filterOptions.price.min, priceMax)
          : [filterOptions.price.min, priceMax]
      );
    }
  }, [
    appliedResetId,
    resetId,
    sessionKind,
    filterOptions.nights.min,
    filterOptions.nights.max,
    filterOptions.price.min,
    filterOptions.price.max,
  ]);

  const filterState = useMemo(
    () =>
      buildFilterState({
        packageTypeIds: selection.packageTypeIds,
        accommodationTypeIds: selection.accommodationTypeIds,
        travelStyleIds: selection.travelStyleIds,
        cityIds: selection.cityIds,
        cancellationPolicyIds: selection.cancellationPolicyIds,
        availableMonths: selection.availableMonths,
        flights: selection.flightValues,
        nightOptionValues: selection.nightOptionValues,
        nightsRange: duration || [],
        budgetRange: budget || [],
        reviewRating: selection.reviewRating,
        hotelRatings: selection.hotelRatings,
        visaIncluded: selection.visaIncluded,
        activityIncluded: selection.activityIncluded,
        filterBounds: {
          nightMin: filterOptions.nights.min,
          nightMax: filterOptions.nights.max,
          budgetMin: filterOptions.price.min,
          budgetMax: filterOptions.price.max,
        },
      }),
    [selection, filterOptions, duration, budget]
  );

  const clearSelection = () => {
    setSelection({
      ...EMPTY_SELECTION,
      ...urlFilterDefaults,
    });
    setDuration([filterOptions.nights.min, filterOptions.nights.max]);
    setBudget([filterOptions.price.min, filterOptions.price.max]);
  };

  const handlers = {
    onTogglePackageType: (id) =>
      setSelection((prev) => ({
        ...prev,
        packageTypeIds: toggleId(prev.packageTypeIds, id),
      })),
    onToggleAccommodation: (id) =>
      setSelection((prev) => ({
        ...prev,
        accommodationTypeIds: toggleId(prev.accommodationTypeIds, id),
      })),
    onToggleTravelStyle: (id) =>
      setSelection((prev) => ({
        ...prev,
        travelStyleIds: toggleId(prev.travelStyleIds, id),
      })),
    onToggleCity: (id) =>
      setSelection((prev) => ({ ...prev, cityIds: toggleId(prev.cityIds, id) })),
    onToggleFlight: (value) =>
      setSelection((prev) => ({
        ...prev,
        flightValues: selectSingleValue(prev.flightValues, value),
      })),
    onToggleNightOption: (value) =>
      setSelection((prev) => ({
        ...prev,
        nightOptionValues: toggleValue(prev.nightOptionValues, value),
      })),
    onReviewRating: (value) =>
      setSelection((prev) => ({ ...prev, reviewRating: value })),
    onToggleHotelRating: (stars) =>
      setSelection((prev) => ({
        ...prev,
        hotelRatings: toggleId(prev.hotelRatings, stars),
      })),
    onToggleCancellationPolicy: (id) =>
      setSelection((prev) => ({
        ...prev,
        cancellationPolicyIds: toggleId(prev.cancellationPolicyIds, id),
      })),
    onToggleVisa: (value) =>
      setSelection((prev) => ({
        ...prev,
        visaIncluded: prev.visaIncluded === value ? "" : value,
      })),
    onToggleActivity: (value) =>
      setSelection((prev) => ({
        ...prev,
        activityIncluded: prev.activityIncluded === value ? "" : value,
      })),
    onToggleMonth: (value) =>
      setSelection((prev) => ({
        ...prev,
        availableMonths: toggleValue(prev.availableMonths, value),
      })),
    onDuration: (range) => {
      setDuration(range);
      setSelection((prev) => ({ ...prev, nightOptionValues: [] }));
    },
    onBudget: setBudget,
    onClear: clearSelection,
  };

  return {
    selection,
    duration: duration || [filterOptions.nights.min, filterOptions.nights.max],
    budget: budget || [filterOptions.price.min, filterOptions.price.max],
    filterState,
    handlers,
    sessionReady: appliedResetId === resetId && duration != null && budget != null,
    searchKey,
    searchEpoch,
  };
}

export default function HolidayListing({
  initialPackages = null,
  initialFilters = null,
  initialUrlParams = null,
  initialListPageHeading = null,
}) {
  const { isDesktop, mounted } = useIsDesktop(901);
  const router = useRouter();
  const dispatch = useAppDispatch();
  const {
    packages: storePackages,
    listMeta,
    loading,
    loadingMore,
    error,
    filters: storeFilters,
  } = useAppSelector((state) => state.holiday);

  const packages = storePackages?.length ? storePackages : initialPackages || [];
  const isLoading = loading;
  const [pageIndex, setPageIndex] = useState(1);
  const listQueryKeyRef = useRef("");
  const filtersLoaded = Boolean(storeFilters || initialFilters);

  const urlParams = useMemo(() => {
    if (!router.isReady) return initialUrlParams || {};
    return parseUrlListParams(router.query);
  }, [router.isReady, router.query, initialUrlParams]);

  const apiUrlParams = useMemo(() => pickUrlApiParams(urlParams), [urlParams]);
  const filterQueryKey = useMemo(
    () => JSON.stringify(router.query || {}),
    [router.query]
  );

  const apiFilters = useMemo(() => {
    const source = storeFilters || initialFilters;
    return normalizeHolidayFilters(source);
  }, [storeFilters, initialFilters]);

  const filterOptions = useMemo(
    () => ({
      packageTypes: apiFilters.packageTypes,
      accommodationTypes: apiFilters.accommodationTypes,
      travelStyles: apiFilters.travelStyles,
      cities: apiFilters.cities,
      flights: apiFilters.flights,
      hotelRatings: apiFilters.hotelRatings,
      reviewBands: buildReviewBands(apiFilters.reviewRatings),
      cancellationPolicies: apiFilters.cancellationPolicies,
      visaIncluded: apiFilters.visaIncluded,
      activityIncluded: apiFilters.activityIncluded,
      availableMonths: apiFilters.availableMonths,
      // nightOptions: apiFilters.nightOptions,
      nights: apiFilters.nights,
      price: apiFilters.price,
      sortOptions: apiFilters.sortOptions,
      totalPackages: apiFilters.totalPackages,
      originCountry: apiFilters.originCountry,
    }),
    [apiFilters]
  );

  const { selection, duration, budget, filterState, handlers, sessionReady, searchKey, searchEpoch } =
    useFilterSelection(filterOptions, urlParams, "holiday");

  const defaultSort = filterOptions.sortOptions[0]?.value || "recommended";
  const [sortBy, setSortBy] = useState(defaultSort);
  const [activeSheet, setActiveSheet] = useState(null);
  const [draftSort, setDraftSort] = useState(defaultSort);
  const [draftBudget, setDraftBudget] = useState(budget);
  const [draftDuration, setDraftDuration] = useState(duration);
  const [draftFlightValues, setDraftFlightValues] = useState([]);
  const [draftHotelRatings, setDraftHotelRatings] = useState([]);
  const [openSections, setOpenSections] = useState({
    themes: false,
    accommodations: false,
    travelStyles: false,
    cities: false,
    reviews: false,
    cancellation: false,
    months: false,
  });
  const [searchLoading, setSearchLoading] = useState(false);
  const [monthError, setMonthError] = useState(false);
  const awaitingSearchLoadRef = useRef(false);
  const sawSearchFetchRef = useRef(false);

  useEffect(() => {
    const stored = loadListingFilters("holiday");
    const storedEpoch = Number(stored?.epoch) || 0;
    if (
      searchEpoch <= storedEpoch &&
      stored?.sortBy &&
      isSameListingSearch(stored, "holiday", searchKey)
    ) {
      setSortBy(stored.sortBy);
      return;
    }
    setSortBy(filterOptions.sortOptions[0]?.value || "recommended");
  }, [searchKey, searchEpoch, filterOptions.sortOptions]);

  useEffect(() => {
    if (filterOptions.sortOptions.length) {
      setSortBy((prev) =>
        filterOptions.sortOptions.some((option) => option.value === prev)
          ? prev
          : filterOptions.sortOptions[0].value
      );
    }
  }, [filterOptions.sortOptions]);

  useEffect(() => {
    if (!sessionReady) return;
    if (filterOptions.nights.max <= 0 && filterOptions.price.max <= 0) return;
    saveListingFilters("holiday", {
      selection,
      duration,
      budget,
      sortBy,
      query: router.query,
      searchKey,
      epoch: searchEpoch,
      boundsKey: getFilterBoundsKey({
        nightMin: filterOptions.nights.min,
        nightMax: filterOptions.nights.max,
        priceMin: filterOptions.price.min,
        priceMax: filterOptions.price.max,
      }),
    });
  }, [
    sessionReady,
    selection,
    duration,
    budget,
    sortBy,
    router.query,
    searchKey,
    searchEpoch,
    filterOptions.nights.min,
    filterOptions.nights.max,
    filterOptions.price.min,
    filterOptions.price.max,
  ]);

  useEffect(() => {
    if (!router.isReady && !initialUrlParams) return;
    dispatch(fetchHolidayFilters(router.query));
  }, [dispatch, router.isReady, filterQueryKey, initialUrlParams]);

  const listQueryKey = useMemo(
    () =>
      JSON.stringify({
        apiUrlParams,
        filterState,
        sortBy,
      }),
    [apiUrlParams, filterState, sortBy]
  );

  const fetchPackagesPage = useCallback(
    (page, append) => {
      dispatch(
        fetchHolidaysList({
          ...buildHolidayListPayload({
            urlParams: apiUrlParams,
            filterState,
            pageIndex: page,
            pageSize: PAGE_SIZE,
            sortBy,
          }),
          append,
        })
      );
    },
    [dispatch, apiUrlParams, filterState, sortBy]
  );

  useEffect(() => {
    if (!router.isReady && !initialUrlParams) return;
    if (!filtersLoaded) return;

    const queryChanged = listQueryKeyRef.current !== listQueryKey;
    listQueryKeyRef.current = listQueryKey;

    if (queryChanged) {
      setPageIndex(1);
      fetchPackagesPage(1, false);
    }
  }, [
    router.isReady,
    initialUrlParams,
    filtersLoaded,
    listQueryKey,
    fetchPackagesPage,
  ]);

  const mappedPackages = useMemo(
    () => (Array.isArray(packages) ? packages.map(mapHolidayPackage) : []),
    [packages]
  );

  const hasListResult = filtersLoaded && !isLoading;
  const isListLoading =
    (!router.isReady && !initialUrlParams) || !filtersLoaded || isLoading || searchLoading;
  const totalCount = hasListResult
    ? listMeta.totalCount
    : filterOptions.totalPackages || mappedPackages.length;
  const hasMore =
    mappedPackages.length > 0 &&
    (totalCount > mappedPackages.length ||
      (listMeta.totalPages > 0 && pageIndex < listMeta.totalPages));

  const loadMorePackages = useCallback(() => {
    if (isLoading || loadingMore || !hasMore) return;
    const nextPage = pageIndex + 1;
    setPageIndex(nextPage);
    fetchPackagesPage(nextPage, true);
  }, [isLoading, loadingMore, hasMore, pageIndex, fetchPackagesPage]);

  const pageTitle = getUrlPageTitle(urlParams);

  const {
    draft: searchDraft,
    locationOpen,
    initDraft,
    openLocationSearch,
    closeLocationSearch,
    selectLocation,
    setMonth: setSearchMonth,
    setDestinationQuery,
    applySearch,
  } = useHolidayEditSearch({
    urlParams,
    pageTitle,
  });

  const handleSearch = useCallback(() => {
    const result = applySearch();
    if (result?.success === false) {
      if (result.error === "month") setMonthError(true);
      return;
    }

    setMonthError(false);
    awaitingSearchLoadRef.current = true;
    sawSearchFetchRef.current = false;
    setSearchLoading(true);
  }, [applySearch]);

  const handleSearchMonthChange = useCallback(
    (month) => {
      setMonthError(false);
      setSearchMonth(month);
    },
    [setSearchMonth]
  );

  const effectiveSearchMonth = useMemo(() => {
    if (!isSameSearchDestination(searchDraft.selection, urlParams)) {
      return String(searchDraft.month || "").trim();
    }
    return resolveEffectiveSearchMonth(searchDraft, urlParams);
  }, [searchDraft, urlParams]);

  useEffect(() => {
    if (effectiveSearchMonth) {
      setMonthError(false);
    }
  }, [effectiveSearchMonth]);

  useEffect(() => {
    if (!awaitingSearchLoadRef.current) return;

    if (isLoading) {
      sawSearchFetchRef.current = true;
      return;
    }

    if (sawSearchFetchRef.current) {
      awaitingSearchLoadRef.current = false;
      sawSearchFetchRef.current = false;
      setSearchLoading(false);
    }
  }, [isLoading, listQueryKey]);

  useEffect(() => {
    if (!searchLoading) return undefined;

    const timeout = window.setTimeout(() => {
      if (!awaitingSearchLoadRef.current) return;
      awaitingSearchLoadRef.current = false;
      sawSearchFetchRef.current = false;
      setSearchLoading(false);
    }, 12000);

    return () => window.clearTimeout(timeout);
  }, [searchLoading]);

  const activeMonthKey = urlParams.month || selection.availableMonths[0] || "";
  const headerSearch = useMemo(
    () => ({
      location: getHeaderSearchSummary(urlParams, pageTitle).location,
      month: resolveSearchMonthLabel(activeMonthKey, filterOptions.availableMonths),
    }),
    [urlParams, pageTitle, filterOptions.availableMonths, activeMonthKey]
  );

  const resultCount = totalCount;

  const rangeProps = {
    nights: filterOptions.nights,
    price: filterOptions.price,
    duration,
    budget,
    hasNightRange: filterOptions.nights.max > filterOptions.nights.min,
    hasPriceRange: filterOptions.price.max > filterOptions.price.min,
  };

  const filterProps = {
    options: filterOptions,
    selection,
    ranges: rangeProps,
    openSections,
    handlers,
    onToggleSection: (key) =>
      setOpenSections((prev) => ({ ...prev, [key]: !prev[key] })),
  };

  const budgetStep =
    filterOptions.price.max - filterOptions.price.min > 1000
      ? Math.max(1, Math.round((filterOptions.price.max - filterOptions.price.min) / 100))
      : 1;

  const isSortActive = sortBy !== defaultSort;
  const isBudgetActive =
    budget[0] !== filterOptions.price.min || budget[1] !== filterOptions.price.max;
  const isDurationActive =
    duration[0] !== filterOptions.nights.min || duration[1] !== filterOptions.nights.max;
  const isFlightsActive = selection.flightValues.length > 0;
  const isHotelRatingActive = selection.hotelRatings.length > 0;
  const isFiltersActive =
    selection.packageTypeIds.length > 0 ||
    selection.accommodationTypeIds.length > 0 ||
    selection.travelStyleIds.length > 0 ||
    selection.cityIds.length > 0 ||
    selection.cancellationPolicyIds.length > 0 ||
    selection.reviewRating > 0 ||
    selection.visaIncluded ||
    selection.activityIncluded ||
    selection.availableMonths.length > 0 ||
    isBudgetActive ||
    isDurationActive;

  const openMobileSheet = (sheet) => {
    if (sheet === "sort") setDraftSort(sortBy);
    if (sheet === "budget") setDraftBudget(budget);
    if (sheet === "duration") setDraftDuration(duration);
    if (sheet === "flights") setDraftFlightValues(selection.flightValues);
    if (sheet === "hotelRating") setDraftHotelRatings(selection.hotelRatings);
    if (sheet === "search") {
      closeLocationSearch();
      initDraft();
    }
    setActiveSheet(sheet);
  };

  const closeMobileSheet = () => setActiveSheet(null);

  const applySortSheet = () => {
    setSortBy(draftSort);
    closeMobileSheet();
  };

  const applyBudgetSheet = () => {
    handlers.onBudget(draftBudget);
    closeMobileSheet();
  };

  const applyDurationSheet = () => {
    handlers.onDuration(draftDuration);
    closeMobileSheet();
  };

  const applySearchSheet = () => {
    closeMobileSheet();
    handleSearch();
  };

  const syncDraftList = (current, draft, toggleHandler) => {
    current.forEach((value) => {
      if (!draft.includes(value)) toggleHandler(value);
    });
    draft.forEach((value) => {
      if (!current.includes(value)) toggleHandler(value);
    });
  };

  const applyFlightsSheet = () => {
    syncDraftList(selection.flightValues, draftFlightValues, handlers.onToggleFlight);
    closeMobileSheet();
  };

  const applyHotelRatingSheet = () => {
    syncDraftList(selection.hotelRatings, draftHotelRatings, handlers.onToggleHotelRating);
    closeMobileSheet();
  };

  useEffect(() => {
    if (!mounted || !isDesktop || !router.isReady) return;
    initDraft();
  }, [mounted, isDesktop, router.isReady, initDraft]);

  useBodyScrollLock(
    Boolean(activeSheet) || Boolean(locationOpen && !isDesktop)
  );

  return (
    <MainLayout hideHeader mobileListing>
      <div className={styles.page}>
        {mounted && isDesktop ? (
          <div className={`standard-padding`} style={{ background: "#FAFAFA", paddingTop: "20px", paddingBottom: "20px" }}>
            <DesktopListingSearch
              urlParams={urlParams}
              pageTitle={pageTitle}
              selection={searchDraft.selection}
              draftLabel={searchDraft.label}
              months={searchDraft.months}
              selectedMonth={effectiveSearchMonth}
              onMonthChange={handleSearchMonthChange}
              onSearch={handleSearch}
              isSearching={searchLoading}
              locationOpen={locationOpen}
              onDestinationOpen={openLocationSearch}
              onDestinationQueryChange={setDestinationQuery}
              onLocationClose={closeLocationSearch}
              onLocationSelect={selectLocation}
              monthError={monthError}
            />
          </div>
        ) : null}
        <Breadcrumbs />
        <div className={`standard-padding ${styles.desktopOnly}`}>
          <div className={styles.layout}>
            <aside className={styles.sidebar}>
              <FiltersPanel {...filterProps} showApply={false} />
            </aside>

            <div className={styles.main}>
              <div className={styles.resultsHead}>
                <p className={styles.count}>
                  {isListLoading
                    ? "Loading packages..."
                    : `${resultCount} Packages`}
                </p>
                {filterOptions.sortOptions.length ? (
                  <label className={styles.sortWrap}>
                    <span className={styles.sortBy}>
                      <BiSort className={styles.sortIcon} aria-hidden />
                      Sort By
                    </span>
                    <span className={styles.sortValueWrap}>
                      <select
                        className={styles.sortSelect}
                        value={sortBy}
                        onChange={(event) => setSortBy(event.target.value)}
                      >
                        {filterOptions.sortOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <IoChevronDown
                        className={styles.sortChevron}
                        aria-hidden
                      />
                    </span>
                  </label>
                ) : null}
              </div>

              <div className={styles.list}>
                {error ? <p className={styles.empty}>{String(error)}</p> : null}
                {isListLoading ? <ListingLoader /> : null}
                {!isListLoading &&
                  !error &&
                  mappedPackages.map((item) => (
                    <PackageCard key={item.id} item={item} />
                  ))}
                {!isListLoading &&
                  !error &&
                  !mappedPackages.length ? (
                  <p className={styles.empty}>
                    No packages match your filters.
                  </p>
                ) : null}
                {!isListLoading && !error ? (
                  <ListLoadSentinel
                    hasMore={hasMore}
                    loading={isLoading}
                    loadingMore={loadingMore}
                    onLoadMore={loadMorePackages}
                  />
                ) : null}
              </div>
            </div>
          </div>
        </div>

        <div className={styles.mobileOnly}>
          <header className={styles.mobileHeader}>
            <a
              href={withRegionPath("/holiday")}
              className={styles.iconBtn}
              aria-label="Back"
            >
              <IoArrowBackSharp fontSize={24} />
            </a>
            <button
              type="button"
              className={styles.searchPill}
              onClick={() => openMobileSheet("search")}
            >
              <span className={styles.searchPillText}>
                <span className={styles.searchPillLocation}>
                  {headerSearch.location}
                </span>
                {headerSearch.month ? (
                  <small>{headerSearch.month}</small>
                ) : null}
              </span>
              <span className={styles.pencil} aria-hidden="true">
                <BiSolidEditAlt fontSize={20} />
              </span>
            </button>
            <CartNavLink className={styles.iconBtn} />
          </header>

          {isListLoading ? (
            <ListingLoader fullPage />
          ) : (
            <>
              <div className={styles.chipBar}>
                {filterOptions.sortOptions.length ? (
                  <button
                    type="button"
                    className={`${styles.chip} ${isSortActive ? styles.chipActive : ""}`}
                    onClick={() => openMobileSheet("sort")}
                  >
                    Sort <BiSort />
                  </button>
                ) : null}
                <button
                  type="button"
                  className={`${styles.chip} ${isFiltersActive ? styles.chipActive : ""}`}
                  onClick={() => openMobileSheet("filters")}
                >
                  {" "}
                  Filters
                  <HiOutlineAdjustmentsHorizontal />
                </button>
                {rangeProps.hasPriceRange ? (
                  <button
                    type="button"
                    className={`${styles.chip} ${isBudgetActive ? styles.chipActive : ""}`}
                    onClick={() => openMobileSheet("budget")}
                  >
                    Budget <IoChevronDown />
                  </button>
                ) : null}
                {rangeProps.hasNightRange ? (
                  <button
                    type="button"
                    className={`${styles.chip} ${isDurationActive ? styles.chipActive : ""}`}
                    onClick={() => openMobileSheet("duration")}
                  >
                    Duration <IoChevronDown />
                  </button>
                ) : null}
                {filterOptions.flights.length ? (
                  <button
                    type="button"
                    className={`${styles.chip} ${isFlightsActive ? styles.chipActive : ""}`}
                    onClick={() => openMobileSheet("flights")}
                  >
                    Flights <IoChevronDown />
                  </button>
                ) : null}
                {filterOptions.hotelRatings.length ? (
                  <button
                    type="button"
                    className={`${styles.chip} ${isHotelRatingActive ? styles.chipActive : ""}`}
                    onClick={() => openMobileSheet("hotelRating")}
                  >
                    Hotel Rating <IoChevronDown />
                  </button>
                ) : null}
              </div>

              <div className={styles.mobileList}>
                {error ? <p className={styles.empty}>{String(error)}</p> : null}
                {!error &&
                  mappedPackages.map((item) => (
                    <PackageCard key={item.id} item={item} mobile />
                  ))}
                {!error && !mappedPackages.length ? (
                  <p className={styles.empty}>
                    No packages match your filters.
                  </p>
                ) : null}
                {!error ? (
                  <ListLoadSentinel
                    hasMore={hasMore}
                    loading={isLoading}
                    loadingMore={loadingMore}
                    onLoadMore={loadMorePackages}
                  />
                ) : null}
              </div>
            </>
          )}

          {activeSheet === "sort" ? (
            <SortSheet
              options={filterOptions.sortOptions}
              value={draftSort}
              onChange={setDraftSort}
              onClose={closeMobileSheet}
              onApply={applySortSheet}
            />
          ) : null}

          {activeSheet === "budget" ? (
            <RangeFilterSheet
              title="Budget"
              onClose={closeMobileSheet}
              onApply={applyBudgetSheet}
            >
              <DualRangeSlider
                min={filterOptions.price.min}
                max={filterOptions.price.max}
                step={budgetStep}
                value={draftBudget}
                onChange={setDraftBudget}
                fixedLabels
                formatLabel={(val) => (
                  <>
                    {getCurrencySymbol("main", 12)}
                    {formatIndianPrice(val)}
                  </>
                )}
              />
            </RangeFilterSheet>
          ) : null}

          {activeSheet === "duration" ? (
            <RangeFilterSheet
              title="Duration"
              onClose={closeMobileSheet}
              onApply={applyDurationSheet}
            >
              <DualRangeSlider
                min={filterOptions.nights.min}
                max={filterOptions.nights.max}
                step={1}
                value={draftDuration}
                onChange={setDraftDuration}
                formatLabel={(val) => `${val}N`}
                fixedLabels
              />
            </RangeFilterSheet>
          ) : null}

          {activeSheet === "flights" ? (
            <FlightsSheet
              options={filterOptions.flights}
              selectedValues={draftFlightValues}
              onToggle={(value) =>
                setDraftFlightValues((prev) => selectSingleValue(prev, value))
              }
              onClose={closeMobileSheet}
              onApply={applyFlightsSheet}
            />
          ) : null}

          {activeSheet === "hotelRating" ? (
            <HotelRatingSheet
              options={filterOptions.hotelRatings}
              selectedRatings={draftHotelRatings}
              onToggle={(stars) =>
                setDraftHotelRatings((prev) => toggleId(prev, stars))
              }
              onClose={closeMobileSheet}
              onApply={applyHotelRatingSheet}
            />
          ) : null}

          {activeSheet === "search" ? (
            <>
              <SearchSheet
                destination={
                  searchDraft.label ||
                  pageTitle ||
                  "Holidays"
                }
                months={searchDraft.months}
                selectedMonth={effectiveSearchMonth}
                onMonthChange={handleSearchMonthChange}
                onDestinationClick={openLocationSearch}
                onClose={closeMobileSheet}
                onApply={applySearchSheet}
                isSearching={searchLoading}
                monthError={monthError}
              />
              {locationOpen ? (
                <LocationSearchScreen
                  initialQuery={searchDraft.label || pageTitle || ""}
                  onClose={closeLocationSearch}
                  onSelect={selectLocation}
                />
              ) : null}
            </>
          ) : null}

          {activeSheet === "filters" ? (
            <MobileSheet
              title="Filters"
              onClose={closeMobileSheet}
              onClear={handlers.onClear}
              onApply={closeMobileSheet}
            >
              <FiltersPanel {...filterProps} showApply={false} compact />
            </MobileSheet>
          ) : null}
        </div>
        <DownloadNH />
        <SeeTheWorld
          title={initialListPageHeading?.heading?.title}
          text={initialListPageHeading?.heading?.description}
        />
      </div>
    </MainLayout>
  );
}